/**
 * Tab Link Component
 * @module components/layout/tabs/TabLink
 */

import { DISABLED_REASONS } from "@lib/tabs/config";
import type { DisabledReason, Tab } from "@lib/tabs/types";
import { Link } from "@tanstack/react-router";

/**
 * Tab link props
 */
export interface TabLinkProps {
  tab: Tab;
}

/**
 * TabLink — unified compact tab link
 * Used everywhere: dashboard and module tabs
 */
export function TabLink({ tab }: TabLinkProps) {
  const Icon = tab.icon;

  return (
    <Link
      to={tab.isDisabled ? undefined : (tab.path as "/" | `/${string}`)}
      disabled={tab.isDisabled}
      activeOptions={tab.useExactMatch ? { exact: true } : undefined}
      className={["tab-base", tab.isDisabled ? "is-disabled" : ""].filter(Boolean).join(" ")}
      activeProps={{
        className: "is-active",
      }}
      title={
        tab.isDisabled && tab.disabledReason
          ? DISABLED_REASONS[tab.disabledReason as DisabledReason]
          : undefined
      }
      aria-selected={tab.isActive}
      aria-disabled={tab.isDisabled}
    >
      {/* Icon */}
      {Icon && <Icon className="tab-icon h-3.5 w-3.5" aria-hidden="true" />}

      {/* Label */}
      <span className="tab-label">{tab.title}</span>

      {/* Badge */}
      {tab.badge && <span className="tab-badge">{tab.badge}</span>}

      {/* Animated underline indicator */}
      <span className="tab-indicator" aria-hidden="true" />
    </Link>
  );
}
